import React, { useRef, useState, ComponentType } from "react";
import dynamic from "next/dynamic";
import styles from "./index.module.scss";

import { PlayIcon, PauseIcon } from "@/components/Atoms/Vectors";
import { FullBackgroundImage } from "../ImageCard";

// MuxPlayer props type for TypeScript
interface MuxPlayerProps {
	id?: string;
	playbackId: string;
	poster?: string;
	className?: string;
	style?: Record<string, any>;
	playsInline?: boolean;
	loop?: boolean;
	muted?: boolean;
	autoPlay?: boolean;
	onPlay?: () => void;
	onPause?: () => void;
	onEnded?: () => void;
}

// Dynamic import to avoid ESM/CJS issues with Next.js 12
// MuxPlayer must be loaded client-side only
// @ts-ignore - Module resolution issue with mux-player-react types
const MuxPlayer: ComponentType<MuxPlayerProps> = dynamic(
	// @ts-ignore
	() => import("@mux/mux-player-react").then((mod) => mod.default),
	{
		ssr: false,
		loading: () => <div className="w-full h-full bg-black animate-pulse" />,
	}
);

interface Props {
	file: any;
	color: any[any];
	className: string;
	onClick: any;
	poster?: any;
}

interface VideoCardProps extends Props {
	controls: any[any];
	color: any[any];
	bgColor?: any;
	className: string;
	placement: string;
	customIcon?: any;
}

/**
 * Extracts the Mux playback ID from a streaming URL
 * @param streamingUrl - URL in format https://stream.mux.com/{playbackId}.m3u8
 * @returns The playback ID or null if not found
 */
function extractPlaybackId(streamingUrl: string | undefined): string | null {
	if (!streamingUrl) return null;
	const match = streamingUrl.match(/stream\.mux\.com\/([^.]+)/);
	return match ? match[1] : null;
}

export function VideoHeroSlider({
	file,
	color,
	className,
	onClick,
	poster,
}: Props) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [playing, setPlaying] = useState(false);
	const playbackId = extractPlaybackId(file?.streamingUrl);
	const playerId = `mux-hero-${file?.muxAssetId || "player"}`;

	const handlePlayPause = () => {
		// For MuxPlayer, access via DOM since dynamic import doesn't support refs
		const muxPlayer = document.getElementById(playerId) as any;
		if (muxPlayer) {
			if (muxPlayer.paused || muxPlayer.ended) {
				muxPlayer.play();
			} else {
				muxPlayer.pause();
			}
			return;
		}

		// For native video fallback
		if (videoRef.current) {
			if (videoRef.current.paused || videoRef.current.ended) {
				videoRef.current.play();
			} else {
				videoRef.current.pause();
			}
		}
	};

	const handlePlay = () => {
		setPlaying(true);
	};

	const handlePause = () => {
		setPlaying(false);
	};

	const handleEnded = () => {
		setPlaying(false);
		if (onClick) onClick();
	};

	// Hero slider style - fullscreen cover, no controls (always cover since no controls)
	const muxPlayerStyle: Record<string, any> = {
		"--media-object-fit": "cover",
		"--media-object-position": "center",
		"--controls": "none",
		"--loading-indicator": "none",
		width: "100%",
		height: "100%",
	};

	return (
		<>
			<button
				onClick={(e) => {
					handlePlayPause();
					if (onClick) onClick(e);
				}}
				aria-label="Video play/pause"
				className={`${
					!playing
						? " left-0 right-0 w-fit mx-auto  top-[10rem] bottom-0 scale-[2] hover:scale-[2.5] transition-all ease-in-out duration-300"
						: "justify-center lg:justify-end mx-auto  left-10  bottom-6 flex flex-row gap-8 md:gap-12"
				} absolute z-30`}
				type="button"
				data-state={playing ? "pause" : "play"}
			>
				{" "}
				{!playing ? <PlayIcon color={color} /> : <PauseIcon color={color} />}
			</button>
			{!playing && (
				<span
					className={`${styles.gradient} absolute w-full h-full z-[1] `}
				></span>
			)}
			{playbackId ? (
				<MuxPlayer
					id={playerId}
					playbackId={playbackId}
					poster={poster?.responsiveImage?.src ? `${poster.responsiveImage.src}?&fit_mode=smartcrop` : undefined}
					className={`${className} ${styles.animate} ${styles.obCover} w-full h-full max-w-[100%] cursor-pointer absolute`}
					style={muxPlayerStyle}
					playsInline
					onPlay={handlePlay}
					onPause={handlePause}
					onEnded={handleEnded}
				/>
			) : (
				// Fallback to native video if no playback ID available
				<video
					ref={videoRef}
					playsInline
					onEnded={onClick}
					onPlay={handlePlay}
					onPause={handlePause}
					className={`${className} ${styles.animate} ${styles.obCover} w-full h-full max-w-[100%] cursor-pointer absolute`}
					src={file?.mp4High}
					poster={poster?.responsiveImage?.src ? `${poster.responsiveImage.src}?&fit_mode=smartcrop` : undefined}
				/>
			)}
		</>
	);
}

// Helper to parse string booleans from controls array
const parseBoolean = (value: any): boolean => {
	if (typeof value === "boolean") return value;
	if (typeof value === "string") return value.toLowerCase() === "true";
	return Boolean(value);
};

export function VideoCard({
	file,
	controls,
	color,
	className,
	onClick,
	placement,
	poster,
	bgColor,
	customIcon,
}: VideoCardProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [playing, setPlaying] = useState(false);
	const [hasStarted, setHasStarted] = useState(false); // Track if video has been played

	const playbackId = extractPlaybackId(file?.streamingUrl);
	const isHero = placement === "SectionHeroVideoRecord";
	
	// Parse controls as booleans (they come as strings from CMS)
	const isLoop = parseBoolean(controls[0]);
	const isMuted = parseBoolean(controls[1]);
	const hasControls = parseBoolean(controls[2]);
	const isAutoPlay = parseBoolean(controls[4]);
	const playerId = `mux-card-${file?.muxAssetId || "player"}`;

	const handlePlay = () => {
		setPlaying(true);
		setHasStarted(true);
	};
	
	const handlePause = () => {
		setPlaying(false);
		// Don't reset hasStarted - video is just paused, not stopped
	};
	
	const handleEnded = () => {
		setPlaying(false);
		setHasStarted(false); // Reset - show play button again
		if (onClick) onClick();
	};

	const handlePlayButtonClick = () => {
		// For MuxPlayer, access via DOM since dynamic import doesn't support refs
		if (playbackId) {
			const muxPlayer = document.getElementById(playerId) as any;
			if (muxPlayer) {
				muxPlayer.play();
			}
		} else if (videoRef.current) {
			videoRef.current.play();
		}
	};

	// Mux Player styles based on placement and controls
	// Object-fit is "contain" ONLY if hasControls AND playing, otherwise always "cover"
	const getMuxPlayerStyle = (): Record<string, any> => {
		const baseStyle: Record<string, any> = {
			width: "100%",
			height: "100%",
			"--media-object-fit": hasControls && playing ? "contain" : "cover",
			"--media-object-position": "center",
			// Set controls colors to white
			"--media-accent-color": "#666666",     // Progress bar accent
			"--media-range-bar-color": "#666666",  // Progress bar
			"--media-text-color": "#666666",       // Time text
		};

		// Hide controls if not enabled OR if video hasn't started yet (show custom play button)
		if (!hasControls || !hasStarted) {
			baseStyle["--controls"] = "none";
			baseStyle["--loading-indicator"] = "none";
		}

		return baseStyle;
	};

	const videoClassName = `${className} w-full cursor-pointer md:min-h-[400px] ${
		hasControls ? "" : "pointer-events-none"
	} ${isHero ? "object-center h-full" : ""} ${
		hasControls && playing ? "object-contain" : "object-cover"
	}`;

	const posterUrl = poster?.responsiveImage?.src
		? `${poster.responsiveImage.src}?&fit_mode=smartcrop`
		: file?.thumbJpg
		? `${file.thumbJpg}?&time=${controls[3]}&fit_mode=smartcrop`
		: undefined;

	const renderPlayer = () => {
		if (playbackId) {
			return (
				<MuxPlayer
					id={playerId}
					playbackId={playbackId}
					poster={posterUrl}
					className={videoClassName}
					style={{
						...getMuxPlayerStyle(),
						backgroundColor: bgColor?.hex,
					}}
					playsInline
					loop={isLoop}
					muted={isMuted}
					autoPlay={isAutoPlay}
					onPlay={handlePlay}
					onPause={handlePause}
					onEnded={handleEnded}
				/>
			);
		}

		// Fallback to native video if no Mux playback ID
		return (
			<video
				ref={videoRef}
				playsInline
				style={{ backgroundColor: bgColor?.hex }}
				loop={isLoop}
				autoPlay={isAutoPlay}
				muted={isMuted}
				controls={hasControls}
				onPlay={handlePlay}
				onPause={handlePause}
				onEnded={handleEnded}
				className={videoClassName}
				src={file?.mp4High}
				poster={posterUrl}
			/>
		);
	};

	// Overlay logic: hide only when hasControls AND playing
	// If no controls, overlay stays visible even while playing
	const showOverlay = !hasControls || !playing;
	
	// Show custom play button only when video hasn't started or has ended
	// Don't show on pause - let the native controls handle that
	const showPlayButton = hasControls && !hasStarted;

	if (isHero) {
		return (
			<>
				{!hasStarted && (
					<div className={""}>
						<FullBackgroundImage data={poster} />
					</div>
				)}
				{showOverlay && (
					<div
						className={`${styles.overlayVideo} ${styles.overlayVideoHero}`}
					></div>
				)}
				{renderPlayer()}
			</>
		);
	}

	return (
		<>
			{!hasStarted && (
				<div className={""}>
					<FullBackgroundImage data={poster} />
				</div>
			)}
			{showPlayButton && (
				<button
					onClick={handlePlayButtonClick}
					aria-label="video play"
					className={`z-[1000] justify-center lg:justify-end bottom-4 right-12 lg:right-12 lg:bottom-12 m-auto h-fit w-fit flex flex-row gap-8 md:gap-12 absolute xs:scale-75 lg:scale-150`}
					type="button"
					data-state="play"
				>
					{customIcon?.[0] ? (
						<PlayIcon custom={customIcon[1]?.img} color={color} />
					) : (
						<PlayIcon color={color} />
					)}
				</button>
			)}
			{showOverlay && <div className={styles.overlayVideo}></div>}
			{renderPlayer()}
		</>
	);
}
